// Templates' shared builders (cloud ADR 0393, TOM-665; VENDORED.md change 11;
// the contract is cloud/specs/board-pages-v2.md §8). A template writes a
// version 2 page from one board item: static HTML and CSS whose figures all
// come through §4's bindings, so the page draws as today's item did and Neo
// can change any of it. Everything here is pure and deterministic: the same
// source and context give byte-identical output.
//
// The colours are the frame's theme tokens (page.js, themes.js), which mirror
// cloud/web tokens.css: --text is the web's --text-1, --muted its --text-2,
// --quiet its --text-3, --background the card's surface (--surface-card),
// --surface a tile on it, --track the chart track, --grid the chart grid, and
// --tc-series-1 the chart line. A template's own custom properties start
// `--_`, which no reading's name can (a reading's name starts with a letter),
// so none is ever a property the host sets.
//
// Each kind of reading has its own colour (cloud ADR 0396, TOM-678): a part
// with a kind carries `data-domain` (domains.js), and `domainCss` turns it
// into the private `--_c` (the line), `--_cf` (the area), `--_ct` and `--_cb`
// (the area's opacity at its top and its foot) and `--_ci` (the symbol), from
// the frame's `--tc-<kind>` tokens. With no kind they are the coral accent, as
// before. A stale reading's part is grey (`--quiet`), never a kind's colour.

import { pageReadingNameFor } from "../page-rules.js";
import { PAGE_DOMAINS, pageDomainFor } from "./domains.js";

/** Text or an attribute's value, escaped for HTML. */
export function esc(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const VOID = new Set(["br", "hr", "col"]);

/**
 * One element: its attributes in the record's order (null, undefined and
 * false left out; true written empty), then its children, joined. Every
 * element but a void one is closed, SVG ones included, so the browser's parse
 * and the server's agree.
 */
export function h(tag, attrs = {}, ...children) {
  let out = `<${tag}`;
  for (const [name, value] of Object.entries(attrs ?? {})) {
    if (value === null || value === undefined || value === false) continue;
    out += value === true ? ` ${name}=""` : ` ${name}="${esc(value)}"`;
  }
  out += ">";
  if (VOID.has(tag)) return out;
  return `${out}${children.flat(Infinity).join("")}</${tag}>`;
}

/**
 * CSS written readably in a template, packed: comments out, runs of white
 * space one space, and none beside `{`, `}`, `;`, `,` and `>`. A template
 * never writes a descendant pseudo-class (`.a :hover`).
 */
export function css(text) {
  return String(text)
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/\s+/g, " ")
    .replace(/\s*([{};,>])\s*/g, "$1")
    .replace(/;}/g, "}")
    .trim();
}

/** A number for markup: at most three decimals, no trailing zeros, never "-0". */
export function n(value) {
  const r = Math.round(value * 1000) / 1000;
  return Object.is(r, -0) ? "0" : String(r);
}

/**
 * The source's paths as page readings, in its path order: each named by
 * pageReadingNameFor, a path met twice keeping its first name.
 */
export function readingNames(paths) {
  const byPath = new Map();
  const taken = new Set();
  for (const path of paths) {
    if (typeof path !== "string" || byPath.has(path)) continue;
    const name = pageReadingNameFor(path, taken);
    taken.add(name);
    byPath.set(path, name);
  }
  return byPath;
}

/** What the server knows of a path, or the defaults for a path it does not know. */
export function pathFacts(context, path) {
  const facts = context?.paths?.[path];
  return {
    storeUnit: facts?.storeUnit ?? null,
    angle: facts?.angle === true,
    available: facts?.available !== false,
  };
}

/** A path whose value is a fill or charge level, stored as a percentage (web lib/levels.ts). */
export const LEVEL_PATH = /\.(currentLevel|stateOfCharge)$/;
/** A tank whose healthy end is empty (web lib/levels.ts). */
export const LOW_GOOD_PATH = /black|waste|gr[ae]y|sewage|holding|bilge/i;

/**
 * A store unit folded as the protocol's display-units.ts folds it ("degrees"
 * is "deg"), and the radian spellings to "rad".
 */
function foldUnit(storeUnit) {
  const unit = String(storeUnit ?? "").toLowerCase();
  if (unit === "degree" || unit === "degrees" || unit === "°") return "deg";
  if (unit === "radian" || unit === "radians") return "rad";
  return unit;
}

/**
 * A wrapping angle's scale (§4.5.4, §4.3: never wrapped), in its store unit:
 * a relative angle (a wind angle, signed from dead ahead) −180° to 180°,
 * else 0° to 360°; in radians when the path is stored in them.
 */
export function angleRange(path, storeUnit) {
  const relative = /(^|\.)angle[A-Z]\w*$|Angle[A-Z]?\w*$/.test(
    String(path ?? ""),
  );
  const full = foldUnit(storeUnit) === "rad" ? 2 * Math.PI : 360;
  return relative ? [-full / 2, full / 2] : [0, full];
}

/**
 * The instrument widgets' default scale for a path, where the widget names
 * none (widgets.tsx), in store units. A dial is a compass, 0° to 360°
 * (CompassRose turns by the value itself); a gauge and a level are 0 to 100
 * whatever the unit, as GaugeBody and LevelBody default. A path stored in
 * radians keeps its turn in radians.
 */
export function defaultScaleFor(path, storeUnit, widget) {
  if (widget === "dial")
    return foldUnit(storeUnit) === "rad" ? [0, 2 * Math.PI] : [0, 360];
  return [0, 100];
}

/**
 * The card body's base: the page fills the card's body from its top-left
 * corner (the card's own padding is the app's), in the board's type.
 */
export const BASE_CSS = css(`
  .tp { box-sizing: border-box; display: flex; flex-direction: column;
    width: 100%; height: 100%; min-height: 0;
    font: 400 13px/1.3 var(--tc-font-sans); color: var(--text); }
  .tp * { box-sizing: border-box; }
  .tp p { margin: 0; }
`);

/**
 * The card's freshness line (TOM-989): "as of 4m ago", "No reading since
 * 2:31 PM" or "No reading yet", which the host writes into the page's
 * `data-tc-freshness` element. Drawn as the panel card's line under its body
 * was: 12 px, muted, at the page's foot, right-aligned, and nothing while the
 * readings are live. The instrument's own gap sets it off from the face. At
 * night it takes the text's colour: muted words under the red tint read
 * about 2.5:1, under the values' night contrast. The app draws its own line
 * under the card when a page leaves this out or hides it.
 */
export const FRESHNESS_LINE = h("p", {
  class: "tp-fresh",
  "data-tc-freshness": true,
});
export const FRESHNESS_CSS = css(`
  .tp-fresh { margin-top: auto; font-size: 12px; color: var(--muted);
    text-align: right; }
  [data-theme=night] .tp-fresh { color: var(--text); }
  .tp-fresh[data-state=live], .tp-fresh:not([data-state]) { display: none; }
`);

/** A part's kind as its attribute, for h(): `data-domain`, or nothing. */
export const domainOf = (domain) =>
  typeof domain === "string" ? { "data-domain": domain } : {};

/**
 * The colours a page with kinds draws in: the coral accent where a part has
 * no kind, then the kinds the page uses (`domains`), in tokens.json's order,
 * then a stale part's grey. Only what the page needs, so a page stays small:
 * the symbol's colour (`--_ci`) only with `icon`, and no default (`base`
 * false) for a page whose root has its kind. A kind's rule comes after the
 * default, so it wins on the page's root too.
 */
export function domainCss(domains, { icon = false, base = true } = {}) {
  const used = PAGE_DOMAINS.filter((d) => domains.includes(d));
  const i = (value) => (icon ? `;--_ci:${value}` : "");
  const rules = used.map(
    (d) =>
      `[data-domain=${d}]{--_c:var(--tc-${d});--_cf:var(--tc-${d}-fill);--_ct:var(--tc-${d}-fill-top);--_cb:var(--tc-${d}-fill-bottom)${i(`var(--tc-${d}-icon)`)}}`,
  );
  const fallback = base
    ? `.tp{--_c:var(--tc-series-1);--_cf:var(--tc-series-1);--_ct:.18;--_cb:.02${i("var(--tc-series-1)")}}`
    : "";
  return `${fallback}${rules.join("")}.tp [data-quality=stale]{--_c:var(--quiet);--_cf:var(--quiet)${i("var(--quiet)")}}`;
}

/** The kinds of the paths given, for domainCss. */
export const domainsOf = (paths) =>
  paths.map((p) => pageDomainFor(p)).filter((d) => d !== null);

/**
 * An area's fill as the Marina charts draw it (DESIGN_SYSTEM §5): the kind's
 * fill colour fading from its top opacity under the line to its foot
 * opacity at the floor. `id` names the gradient, unique on the page; the
 * gradient's stops take the colours of the svg they sit in, so an svg
 * marked stale greys its area too.
 */
export function areaGradient(id) {
  return h(
    "linearGradient",
    { id, x1: "0", y1: "0", x2: "0", y2: "1" },
    h("stop", { offset: "0", class: "ag0" }),
    h("stop", { offset: "1", class: "ag1" }),
  );
}

/**
 * The area gradient's stops (areaGradient), once a page. An area path takes
 * its gradient as its `fill` attribute (`fill="url(#id)"`), so each drawing
 * names its own; a stale reading's area is a flat grey wash instead.
 */
/**
 * A level track's quarter marks, cut out of the track and its fill as 2 px
 * gaps (TOM-1001), as a CSS `mask-image` value. They were spans painted in
 * the card's colour, which the night tint turns darker than the card: dark
 * notches. A gap shows the card itself in every theme. The mask's colour
 * only sets its alpha.
 */
export const QUARTER_GAPS =
  "linear-gradient(90deg, var(--text) calc(25% - 1px), transparent 0 calc(25% + 1px), var(--text) 0 calc(50% - 1px), transparent 0 calc(50% + 1px), var(--text) 0 calc(75% - 1px), transparent 0 calc(75% + 1px), var(--text) 0)";

export const AREA_CSS = css(`
  .ag0 { stop-color: var(--_cf); stop-opacity: var(--_ct); }
  .ag1 { stop-color: var(--_cf); stop-opacity: var(--_cb); }
  [data-tc-shape=area][data-quality=stale] { fill: var(--quiet); fill-opacity: 0.12; }
`);

/**
 * The range chip (DESIGN_SYSTEM §5, MIGRATION "Range chip"): "24H" beside a
 * mark in the kind's colour, only where the page draws a stored 24 h
 * history. The mark is a text character, so the check reads the chip as
 * "▲ 24H", never as a number beside a value (§9.4).
 */
export function rangeChip(text = "24H") {
  return h(
    "span",
    { class: "rc" },
    h("i", { class: "rc-m" }, "\u25B2"),
    esc(text),
  );
}

/** The range chip's CSS (rangeChip). */
export const CHIP_CSS = css(`
  .rc { flex: none; display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600;
    line-height: 1; color: var(--quiet); white-space: nowrap; }
  .rc-m { font-style: normal; font-size: 8px; color: var(--_c); }
`);

/**
 * The warning mark beside a level's words (DESIGN_SYSTEM §7: a warning is its
 * icon and readable text, never a kind's colour): a triangle with its bar
 * and dot, in the warning's tone (`currentColor`), 14 px. With `ref`, a
 * `<use>` of the mark a page draws once in its defs as `warnShape(ref)`.
 */
export function warnMark(cls = "wm", ref = null) {
  if (ref !== null)
    return h(
      "svg",
      { class: cls, viewBox: "0 0 24 24", "aria-hidden": "true" },
      h("use", { href: `#${ref}` }),
    );
  return h(
    "svg",
    { class: cls, viewBox: "0 0 24 24", "aria-hidden": "true" },
    warnShape(),
  );
}

/** The warning mark's shapes, as a `<g id>` for a page's defs when `id` is given. */
export function warnShape(id = null) {
  const shapes = [
    h("path", {
      d: "M12 3.6 22 20.4H2Z",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2.2",
      "stroke-linejoin": "round",
    }),
    h("rect", {
      x: "10.9",
      y: "9",
      width: "2.2",
      height: "6",
      rx: "1.1",
      fill: "currentColor",
    }),
    h("circle", { cx: "12", cy: "17.2", r: "1.3", fill: "currentColor" }),
  ];
  return id === null ? shapes.join("") : h("g", { id }, shapes);
}

/**
 * The readout's numeral, in the Marina weights (DESIGN_SYSTEM §3, TOM-678):
 * rounded, proportional, 700, and its unit 0.42 of the numeral, never under
 * 11 px, 600, in ink (charts.css drew 600 and a --text-2 unit at 0.4). A missing value is
 * the host's "—" in --text-3, as `.chart-readout--empty` draws it. The
 * numeral's line is 1 em, as today; its glyphs reach past it, so it takes
 * the room back as padding (and gives it up as margin) to hold its text
 * inside its own box, as the rendered check's intact test asks (§9.6). A
 * page whose first line is a numeral pads its top by as much, so the
 * numeral's box stays inside the frame.
 */
export const READOUT_CSS = css(`
  .rd { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; min-width: 0; }
  .rd-row { display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.18em; min-width: 0; }
  .rd-v { font-family: var(--tc-font-rounded); font-weight: 700; line-height: 1;
    color: var(--text); font-variant-numeric: proportional-nums; white-space: nowrap;
    padding-block: 0.15em; margin-block: -0.15em; }
  .rd-u { font-size: max(11px, 0.42em); font-weight: 600; line-height: 1; color: var(--text);
    white-space: nowrap; padding-block: 0.15em; margin-block: -0.15em; }
  .rd-u:empty, .rd-v[data-quality=missing] ~ .rd-u { display: none; }
  .rd-v[data-quality=missing] { color: var(--muted); font-weight: 500; }
`);

/** A readout's row: the reading's value and its unit, at a numeral size in px. */
export function readout(name, size, cls = "") {
  return h(
    "span",
    { class: cls ? `rd-row ${cls}` : "rd-row", style: `font-size:${size}px` },
    h("span", { class: "rd-v", "data-tc-text": name }),
    h("span", { class: "rd-u", "data-tc-unit": name }),
  );
}

/** A template's output, in §8.2's shape. */
export function output(id, version, parts) {
  return {
    id,
    version,
    html: parts.html,
    css: parts.css ?? "",
    readings: parts.readings ?? {},
    live: parts.live ?? [],
    feeds: parts.feeds ?? [],
    card: parts.card,
    bare: parts.bare === true,
  };
}

/** A reading record: its path, then its range and zones when it has them. */
export function reading(path, range, zones) {
  const out = { path };
  if (Array.isArray(range)) out.range = [range[0], range[1]];
  if (Array.isArray(zones) && zones.length > 0) out.zones = zones;
  return out;
}

/** The record's keys sorted, deeply: a look written the same whatever order it came in. */
export function sortKeysDeep(value) {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value === null || typeof value !== "object") return value;
  const out = {};
  for (const key of Object.keys(value).sort())
    out[key] = sortKeysDeep(value[key]);
  return out;
}

/** A finite number. */
export const numeric = (v) => typeof v === "number" && Number.isFinite(v);
